/**
 * Stripe Billing & Subscription Service
 * Powered by centralized apiClient
 */

import apiClient from './apiClient';

/**
 * Create a Stripe checkout session for plan upgrade / subscription
 * @param {Object|string} payload - Plan lookup key or checkout payload
 */
export const createCheckoutSession = async (payload) => {
  try {
    const finalPayload =
      typeof payload === "string"
        ? { lookup_key: payload, mode: "subscription", quantity: 1 }
        : payload;
    const { data } = await apiClient.post("/api/stripe/create-session", finalPayload);
    return data;
  } catch (err) {
    throw err.response?.data || err;
  }
};

/**
 * Retrieve Stripe checkout session details by session ID
 * @param {string} id - Stripe session ID
 */
export const getSession = async (id) => {
  try {
    const { data } = await apiClient.get(`/api/stripe/session/${id}`);
    return data;
  } catch (err) {
    throw err.response?.data || err;
  }
};

/**
 * Verify Stripe checkout session and activate user plan
 * @param {string} session_id
 * @param {string} [token] - Optional user token (automatically injected if omitted)
 * @param {string|null} [plan_id]
 */
export const verifySession = async (session_id, token, plan_id = null) => {
  try {
    const body = { session_id };
    if (plan_id) body.plan_id = plan_id;
    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    const { data } = await apiClient.post("/api/stripe/verify", body, config);
    return data;
  } catch (err) {
    throw err.response?.data || err;
  }
};

/**
 * Fetch current user's latest active Stripe subscription details
 * @param {string} [token] - Optional user token (automatically injected if omitted)
 */
export const getMyLastSubscription = async (token) => {
  try {
    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    const { data } = await apiClient.get(`/api/stripe/my-last-subscription`, config);
    return data;
  } catch (err) {
    throw err.response?.data || err;
  }
};

/**
 * Create a customer portal session for managing Stripe billing
 * @param {string} [token] - Optional user token
 * @param {string|null} [returnUrl]
 */
export const createPortalSession = async (token, returnUrl = null) => {
  try {
    const payload = {};
    if (returnUrl) payload.return_url = returnUrl;

    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    const { data } = await apiClient.post(
      "/api/stripe/create-portal-session",
      payload,
      config
    );
    return data;
  } catch (err) {
    throw err.response?.data || err;
  }
};

/**
 * Preview or apply a subscription tier change
 * @param {string} [token]
 * @param {Object} body
 * @param {boolean} preview
 */
export const changePlan = async (token, body = {}, preview = true) => {
  try {
    const url = `/api/stripe/change-plan${preview ? "?preview=true" : ""}`;
    const config = {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "Content-Type": "application/json",
      },
      timeout: 60000, // 60s timeout for Stripe card charging and multi-step updates
    };
    const { data } = await apiClient.post(url, body, config);
    return data;
  } catch (err) {
    throw err.response?.data || err;
  }
};

export const api = apiClient;
export default apiClient;
